import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const API_BASE = 'https://visionry-api-production.up.railway.app';

async function apiPost(path, body) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
    body: JSON.stringify(body)
  });
  const json = await res.json().catch(() => ({}));
  return { ok: res.ok, ...json };
}

async function apiGet(path) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Authorization': `Bearer ${session?.access_token}` }
  });
  const json = await res.json().catch(() => ({}));
  return { ok: res.ok, ...json };
}

// ── Brand tokens (match visionry.io) ──
const C = {
  bg:       '#061618',
  card:     '#0d3338',
  card2:    '#10434a',
  border:   '#10434a',
  teal:     '#25a2b3',
  tealSoft: '#5fc4d2',
  text:     '#e0f0f2',
  muted:    '#7fb8c0',
  faint:    '#3d5a5f',
  green:    '#00cc88',
  red:      '#ff6b6b',
  off:      '#0a282c'
};

// Mobile breakpoint — below this, setting rows stack vertically (label/hint on
// top, full-width input underneath) instead of cramming into two columns.
const MOBILE_MAX = 640;

// Small hook: tracks whether we're on a narrow screen. WHY: inline styles can't
// use CSS media queries, so we branch the layout in JS off the live window width.
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_MAX : false
  );
  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth <= MOBILE_MAX); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

// ─── UI helpers (brand: #0d3338 cards, #25a2b3 teal accent) ─────────────────

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      style={{
        width: 44, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
        background: checked ? C.teal : C.off, position: 'relative',
        transition: 'background 0.15s', flexShrink: 0
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: checked ? 21 : 3,
        width: 20, height: 20, borderRadius: '50%', background: '#fff',
        transition: 'left 0.15s'
      }} />
    </button>
  );
}

// Row — the core layout. On desktop: label/hint left, control right (side by
// side). On mobile: stacks — label/hint full-width on top, control full-width
// below, so long descriptions read as normal lines instead of a narrow column.
// `tight` keeps small controls (toggles) on the same line as the label even on
// mobile, since a toggle under a label looks odd and wastes space.
function Row({ label, hint, children, isMobile, tight = false }) {
  // A toggle is small enough to stay inline even on mobile.
  const stack = isMobile && !tight;

  return (
    <div style={{
      display: 'flex',
      flexDirection: stack ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: stack ? 'stretch' : 'center',
      padding: '14px 0', borderBottom: `1px solid ${C.border}`,
      gap: stack ? 10 : 16
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, color: C.text }}>{label}</div>
        {hint && <div style={{ fontSize: 12, color: C.muted, marginTop: 3, lineHeight: 1.4 }}>{hint}</div>}
      </div>
      <div style={{
        flexShrink: 0,
        // When stacked, let the control area span the row so full-width inputs fill it.
        width: stack ? '100%' : 'auto',
        display: 'flex',
        justifyContent: stack ? 'flex-start' : 'flex-end'
      }}>
        {children}
      </div>
    </div>
  );
}

function NumberInput({ value, onChange, placeholder, suffix }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        type="number"
        min="0"
        value={value === null || value === undefined ? '' : value}
        placeholder={placeholder}
        onChange={e => {
          const v = e.target.value;
          onChange(v === '' ? null : Math.max(0, parseInt(v, 10) || 0));
        }}
        style={{
          width: 80, padding: '8px 10px', background: C.bg,
          border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
          fontSize: 14, outline: 'none', textAlign: 'right'
        }}
      />
      {suffix && <span style={{ fontSize: 12, color: C.muted, minWidth: 44 }}>{suffix}</span>}
    </div>
  );
}

// Money input — allows one or two decimals (numeric column). WHY separate from
// NumberInput: price/deposit can be e.g. €32.50; NumberInput's parseInt would
// silently round that to 32. We keep the raw string while typing so a trailing
// "." or ".5" isn't fought by the parser, and normalise to a number on save.
function DecimalInput({ value, onChange, placeholder, suffix }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {suffix && <span style={{ fontSize: 12, color: C.muted }}>{suffix}</span>}
      <input
        type="text"
        inputMode="decimal"
        value={value === null || value === undefined ? '' : value}
        placeholder={placeholder}
        onChange={e => {
          const v = e.target.value;
          if (v === '') return onChange(null);
          if (/^\d*\.?\d{0,2}$/.test(v)) onChange(v);
        }}
        style={{
          width: 90, padding: '8px 10px', background: C.bg,
          border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
          fontSize: 14, outline: 'none', textAlign: 'right'
        }}
      />
    </div>
  );
}

// Free-text input (Revolut link, school name, location). On mobile it goes
// full-width (fills the stacked row); on desktop it keeps a fixed width.
function TextInput({ value, onChange, placeholder, isMobile }) {
  return (
    <input
      type="text"
      value={value || ''}
      placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      style={{
        width: isMobile ? '100%' : 220, padding: '10px',
        background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
        color: C.text, fontSize: 14, outline: 'none', boxSizing: 'border-box'
      }}
    />
  );
}

// Read-only display (for fields set at onboarding, e.g. ADI number, name).
function ReadOnly({ value }) {
  return (
    <span style={{ fontSize: 14, color: C.muted }}>{value || '—'}</span>
  );
}

// Native time picker — on mobile this opens the OS time wheel (no typing).
// Reads/writes "HH:MM". WHY native: best mobile UX with zero custom code.
function TimeInput({ value, onChange }) {
  return (
    <input
      type="time"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      style={{
        padding: '8px 10px', background: C.bg,
        border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
        fontSize: 14, outline: 'none'
      }}
    />
  );
}

// Whole-hour dropdown (for lunch — the slot engine only supports on-the-hour
// lunch, so we offer hours only, never half-hours, to match what it reads).
function HourSelect({ value, onChange }) {
  return (
    <select
      value={value ?? ''}
      onChange={e => onChange(e.target.value === '' ? null : parseInt(e.target.value, 10))}
      style={{
        padding: '8px 10px', background: C.bg,
        border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
        fontSize: 14, outline: 'none'
      }}
    >
      <option value="">–</option>
      {Array.from({ length: 24 }, (_, h) => (
        <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
      ))}
    </select>
  );
}

// The seven days, in week order. Code is the 3-letter form the slot engine
// matches against (irishDayName → "MON" etc).
const WEEK_DAYS = [
  { code: 'MON', label: 'Mon' }, { code: 'TUE', label: 'Tue' },
  { code: 'WED', label: 'Wed' }, { code: 'THU', label: 'Thu' },
  { code: 'FRI', label: 'Fri' }, { code: 'SAT', label: 'Sat' },
  { code: 'SUN', label: 'Sun' }
];

function formatEuro(n) {
  return `€${Number(n || 0).toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function formatPayoutDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { timeZone: 'Europe/Dublin', day: 'numeric', month: 'short' });
}

function Card({ title, children }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 14, padding: '4px 20px 8px', marginBottom: 16
    }}>
      <div style={{ fontSize: 13, color: C.muted, padding: '16px 0 4px', textTransform: 'uppercase', letterSpacing: 1 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

export default function Settings({ instructor }) {
  // Visionry Go (mode 'waitlist') shows only "Your details" — every other card
  // configures the booking machinery that Go instructors don't run (yet).
  const isGo = instructor.mode === 'waitlist';
  // Fill (calendar cancellation sync) also skips Visionry's own diary/booking
  // machinery — their diary lives in Google Calendar, not here.
  const isFill = instructor.mode === 'fill';
  // WHY fetch fresh on mount (not just use the prop): the instructor prop is
  // loaded once at login and goes stale after a save. Fetching the row here
  // means the form always shows the true current DB state. RLS scopes this to
  // the logged-in instructor's own row automatically.
  const [form, setForm]     = useState(null);
  const [readonly, setReadonly] = useState({}); // fields shown but not editable
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'ok' | 'err', msg }
  const [stripeOnboarded, setStripeOnboarded] = useState(false);
  const [stripeBusy, setStripeBusy]           = useState(false);
  const [stripeNote, setStripeNote]           = useState(null);
  // Live panel data from GET /connect/summary — null until loaded (or if the
  // call fails / they've never started onboarding), in which case the UI
  // falls back to the flat stripeOnboarded badge below.
  const [stripeSummary, setStripeSummary]         = useState(null);
  const [stripeSummaryLoading, setStripeSummaryLoading] = useState(false);
  // Which payment method's fields are showing — UI-only (not a DB column),
  // so an instructor never sees the Revolut and Stripe boxes at once and
  // fills in both by mistake. Defaults off stripe_onboarded on load, below.
  const [depositMethod, setDepositMethod]     = useState('revolut');
  const isMobile = useIsMobile();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data, error } = await supabase
        .from('instructors')
        .select('*')
        .eq('instructor_key', instructor.instructor_key)
        .single();
      if (cancelled) return;
      if (error || !data) {
        setStatus({ type: 'err', msg: 'Could not load your settings.' });
        return;
      }
      setForm({
        // Business details
        school_name:                      data.school_name ?? '',
        locations: data.locations || (data.location ? [data.location] : []),
        price_per_lesson:                 data.price_per_lesson ?? null,
        slot_minutes:                     data.slot_minutes ?? 60,
        // Schedule — converted from DB integer/CSV/jsonb into form-friendly shapes.
        start_time:  `${String(data.start_hour ?? 8).padStart(2,'0')}:${String(data.start_minute ?? 30).padStart(2,'0')}`,
        end_time:    `${String(data.end_hour ?? 18).padStart(2,'0')}:${String(data.end_minute ?? 30).padStart(2,'0')}`,
        // days_off CSV → Set of 3-letter codes the toggles read.
        days_off_set: new Set(
          (data.days_off || '').split(',').map(d => d.trim().toUpperCase().substring(0,3)).filter(Boolean)
        ),
        // day_end_overrides jsonb → { SAT: "12:30", ... } (keys normalised to 3-letter).
        overrides: Object.fromEntries(
          Object.entries(data.day_end_overrides || {}).map(([k,v]) => [k.toUpperCase().substring(0,3), v])
        ),
        // Lunch — 0/0 means none. Toggle on only if a real lunch is set.
        lunch_enabled: (data.lunch_start > 0 && data.lunch_end > data.lunch_start),
        lunch_start:   data.lunch_start || null,
        lunch_end:     data.lunch_end || null,
        // Taking on students
        accepting_new_students:           data.accepting_new_students ?? true,
        max_active_students:              data.max_active_students ?? null,
        require_approval:                 data.require_approval ?? false,
        // Booking rules
        online_booking_enabled:           data.online_booking_enabled ?? true,
        min_notice_hours:                 data.min_notice_hours ?? 0,
        max_advance_days:                 data.max_advance_days ?? 60,
        buffer_minutes:                   data.buffer_minutes ?? 0,
        max_lessons_per_week_per_student: data.max_lessons_per_week_per_student ?? null,
        cancel_cutoff_hours:              data.cancel_cutoff_hours ?? 24,
        // Payments
        revolut_link:                     data.revolut_link ?? '',
        deposit_enabled:                  data.deposit_enabled ?? false,
        deposit_amount:                   data.deposit_amount ?? 20,
        // Visionry Fill
        calendar_sync_enabled:            data.calendar_sync_enabled ?? false,
        fill_auto_offer:                  data.fill_auto_offer ?? false,
        fill_write_back:                  data.fill_write_back ?? true,
        fill_min_notice_minutes:          data.fill_min_notice_minutes ?? 60,
        roster_auto_sync_enabled:         data.roster_auto_sync_enabled ?? false,
        roster_auto_sync_days:            data.roster_auto_sync_days ?? 14
      });
      setReadonly({
        instructor_name: data.instructor_name,
        adi_number:      data.adi_number,
        instructor_key:  data.instructor_key,
        calendar_connected: !!data.calendar_id,
        calendar_syncing:   !!data.calendar_sync_token
      });
      setStripeOnboarded(data.stripe_onboarded ?? false);
      setDepositMethod(data.stripe_onboarded ? 'stripe' : 'revolut');

      // Live panel — catches a just-completed Stripe onboarding (the
      // instructor may have just landed back here via the return_url
      // redirect, before this DB row necessarily reflects it), and doubles
      // as the earnings/payout panel data. Best-effort: on failure we keep
      // the DB-sourced stripeOnboarded above and render the flat fallback
      // badge instead of the full panel.
      setStripeSummaryLoading(true);
      const summaryRes = await apiGet('/connect/summary');
      if (cancelled) return;
      setStripeSummaryLoading(false);
      if (summaryRes.ok && summaryRes.connected) {
        setStripeSummary(summaryRes);
        setStripeOnboarded(!!summaryRes.chargesEnabled);
        if (summaryRes.chargesEnabled && !data.stripe_onboarded) setDepositMethod('stripe');
      } else if (summaryRes.ok) {
        // connected: false — never started onboarding. Nothing to show.
        setStripeSummary(null);
      }
      // else: the call itself failed (network/500) — stripeSummary stays
      // null and stripeOnboarded keeps its DB-sourced value from above.
    }
    load();
    return () => { cancelled = true; };
  }, [instructor.instructor_key]);

  async function handleStripeOnboard() {
    setStripeBusy(true);
    setStripeNote(null);
    const res = await apiPost('/connect/onboard', {});
    if (res.ok && res.url) {
      window.location.href = res.url;
    } else {
      setStripeBusy(false);
      setStripeNote(res.error || 'Could not start Stripe setup — try again.');
    }
  }

  function set(key, value) {
    setForm(f => ({ ...f, [key]: value }));
    setStatus(null); // any edit clears the last save message
  }

  async function handleSave() {
    setSaving(true);
    setStatus(null);

    // If deposits are on, we need SOMEWHERE to send students to pay — either
    // rail covers it, so only block when neither is set up.
    if (form.deposit_enabled && !(form.revolut_link || '').trim() && !stripeOnboarded) {
      setSaving(false);
      return setStatus({ type: 'err', msg: 'Add your Revolut link, or set up card deposits below, to require a deposit.' });
    }

    // slot_minutes must be sensible — 0 would break slot generation.
    if (!form.slot_minutes || form.slot_minutes < 15) {
      setSaving(false);
      return setStatus({ type: 'err', msg: 'Lesson length must be at least 15 minutes.' });
    }

    // ── Schedule conversions (form shapes → DB formats the slot engine reads) ──
    const [sH, sM] = (form.start_time || '08:30').split(':').map(Number);
    const [eH, eM] = (form.end_time   || '18:30').split(':').map(Number);
    if (eH * 60 + eM <= sH * 60 + sM) {
      setSaving(false);
      return setStatus({ type: 'err', msg: 'Your finish time must be after your start time.' });
    }

    // days_off → CSV of the days toggled OFF (i.e. in the days_off set).
    const daysOffCsv = WEEK_DAYS
      .filter(d => form.days_off_set.has(d.code))
      .map(d => d.code)
      .join(',');

    // Overrides: keep only days still worked, with a valid HH:MM time.
    const cleanOverrides = {};
    for (const [day, time] of Object.entries(form.overrides || {})) {
      if (form.days_off_set.has(day)) continue;            // skip days off
      if (/^\d{2}:\d{2}$/.test(time || '')) cleanOverrides[day] = time;
    }

    // Lunch: only persist if enabled AND valid (whole-hour, end after start).
    let lunchStart = 0, lunchEnd = 0;
    if (form.lunch_enabled) {
      if (form.lunch_start == null || form.lunch_end == null || form.lunch_end <= form.lunch_start) {
        setSaving(false);
        return setStatus({ type: 'err', msg: 'Set a lunch start and a later finish — or turn lunch off.' });
      }
      lunchStart = form.lunch_start;
      lunchEnd   = form.lunch_end;
    }

    // Normalise money fields: stored strings → numbers (or null).
    const priceNum   = form.price_per_lesson === null || form.price_per_lesson === ''
      ? null : Number(form.price_per_lesson);
    const depositNum = form.deposit_amount === null || form.deposit_amount === ''
      ? null : Number(form.deposit_amount);

    // Build the update EXPLICITLY — only real columns, never the form-only keys
    // (days_off_set, overrides, start_time, end_time, lunch_enabled), which would
    // error against the table. Safer than spreading ...form.
    const update = {
      // Business details
      school_name:      (form.school_name || '').trim() || null,
      locations:        form.locations.filter(l => l.trim()).length ? form.locations.map(l => l.trim()).filter(Boolean) : null,
      location:         (form.locations.map(l => l.trim()).filter(Boolean)[0]) || null,
      price_per_lesson: priceNum,
      slot_minutes:     form.slot_minutes,
      // Schedule
      start_hour: sH, start_minute: sM,
      end_hour:   eH, end_minute:   eM,
      days_off:   daysOffCsv || null,
      day_end_overrides: cleanOverrides,
      lunch_start: lunchStart,
      lunch_end:   lunchEnd,
      // Students
      accepting_new_students: form.accepting_new_students,
      max_active_students:    form.max_active_students,
      require_approval:       form.require_approval,
      // Booking rules
      online_booking_enabled:           form.online_booking_enabled,
      min_notice_hours:                 form.min_notice_hours,
      max_advance_days:                 form.max_advance_days,
      buffer_minutes:                   form.buffer_minutes,
      max_lessons_per_week_per_student: form.max_lessons_per_week_per_student,
      cancel_cutoff_hours:              form.cancel_cutoff_hours,
      // Payments
      revolut_link:    (form.revolut_link || '').trim() || null,
      deposit_enabled: form.deposit_enabled,
      deposit_amount:  depositNum,
      // Visionry Fill
      calendar_sync_enabled:    form.calendar_sync_enabled,
      fill_auto_offer:          form.fill_auto_offer,
      fill_write_back:          form.fill_write_back,
      fill_min_notice_minutes:  form.fill_min_notice_minutes,
      roster_auto_sync_enabled: form.roster_auto_sync_enabled,
      roster_auto_sync_days:    form.roster_auto_sync_days,
      updated_at: new Date().toISOString()
    };

    const { error } = await supabase
      .from('instructors')
      .update(update)
      .eq('instructor_key', instructor.instructor_key);

    setSaving(false);
    setStatus(error
      ? { type: 'err', msg: error.message }
      : { type: 'ok', msg: 'Settings saved ✓' });
  }

  if (!form) return <div style={{ color: C.muted, padding: 20, fontFamily: 'Poppins, sans-serif' }}>Loading…</div>;

  return (
    <div style={{ fontFamily: 'Poppins, sans-serif', paddingBottom: 'calc(80px + env(safe-area-inset-bottom))' }}>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>
        Settings — your rules
      </div>

      <Card title="Your details">
        <Row isMobile={isMobile} label="School name"
             hint="Shown to students in messages (e.g. booking confirmations).">
          <TextInput value={form.school_name} onChange={v => set('school_name', v)} placeholder="Your school name" isMobile={isMobile} />
        </Row>
        <div style={{ padding: '14px 0', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 14, color: C.text }}>
            {(isGo || isFill) ? 'Service areas' : 'Pickup locations'}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 3, marginBottom: 10 }}>
            {isGo
              ? 'The areas you cover — shown as a picklist when someone joins your waitlist.'
              : isFill
              ? 'The areas you cover — shown on a freed-slot offer, display only.'
              : 'Where you meet students. Add more than one to give students a location picker when booking.'}
          </div>
          {form.locations.map((loc, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <input
                type="text"
                value={loc}
                onChange={e => {
                  const next = [...form.locations];
                  next[idx] = e.target.value;
                  set('locations', next);
                }}
                placeholder="e.g. Galway city"
                style={{
                  flex: 1, padding: '8px 10px', background: C.bg,
                  border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
                  fontSize: 14, outline: 'none', boxSizing: 'border-box'
                }}
              />
              <button
                type="button"
                onClick={() => set('locations', form.locations.filter((_, i) => i !== idx))}
                style={{ background: 'none', border: 'none', color: C.red, fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}
              >×</button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => set('locations', [...form.locations, ''])}
            style={{
              background: 'none', border: `1px dashed ${C.teal}`, borderRadius: 8,
              color: C.teal, fontSize: 13, padding: '8px 14px', cursor: 'pointer', marginTop: 4
            }}
          >+ Add location</button>
        </div>
        <Row isMobile={isMobile} label="Price per lesson"
             hint="Your standard lesson fee. Used for your records (students still pay you directly).">
          <DecimalInput value={form.price_per_lesson} onChange={v => set('price_per_lesson', v)} suffix="€" placeholder="35" />
        </Row>
        <Row isMobile={isMobile} label="Lesson length"
             hint="How long each lesson slot is. Most EDT lessons are 60 minutes.">
          <NumberInput value={form.slot_minutes} onChange={v => set('slot_minutes', v)} suffix="mins" />
        </Row>
        <Row isMobile={isMobile} label="ADI number"
             hint="Your RSA Approved Driving Instructor number. Set at sign-up — contact us to change it.">
          <ReadOnly value={readonly.adi_number} />
        </Row>
        <Row isMobile={isMobile} label="Instructor name"
             hint="Set at sign-up — contact us to change it.">
          <ReadOnly value={readonly.instructor_name} />
        </Row>
      </Card>

      {isFill && (
        <Card title="Calendar">
          <Row isMobile={isMobile} label="Google Calendar"
               hint="Connected by Visionry when Fill is set up for you — this isn't a self-serve connection yet.">
            <span style={{
              fontSize: 13, fontWeight: 600,
              color: readonly.calendar_connected ? (readonly.calendar_syncing ? C.green : C.tealSoft) : C.red
            }}>
              {readonly.calendar_connected
                ? (readonly.calendar_syncing ? 'Connected — syncing' : 'Connected — starting up')
                : 'Not connected'}
            </span>
          </Row>
          <Row isMobile={isMobile} tight label="Watch for cancellations"
               hint="Poll your Google Calendar every couple of minutes for freed lesson slots.">
            <Toggle checked={form.calendar_sync_enabled} onChange={v => set('calendar_sync_enabled', v)} />
          </Row>
          <Row isMobile={isMobile} tight label="Offer automatically, without asking me first"
               hint="Off by default: every detected cancellation asks you to confirm on WhatsApp before it's offered to your students. Turn on only once you're confident it's reading your calendar correctly.">
            <Toggle checked={form.fill_auto_offer} onChange={v => set('fill_auto_offer', v)} />
          </Row>
          <Row isMobile={isMobile} tight label="Add the replacement student back to my calendar"
               hint="When a freed slot is confirmed, put the new student on your calendar at that time.">
            <Toggle checked={form.fill_write_back} onChange={v => set('fill_write_back', v)} />
          </Row>
          <Row isMobile={isMobile} label="Minimum notice"
               hint="Don't offer a cancellation that leaves less than this much notice.">
            <NumberInput value={form.fill_min_notice_minutes} onChange={v => set('fill_min_notice_minutes', v)} suffix="mins" />
          </Row>
          <Row isMobile={isMobile} tight label="Add my students automatically from my calendar"
               hint="Adds anyone who booked an online lesson with you recently straight to your roster — no manual typing. Never removes anyone; use the Students tab to mark someone as finished.">
            <Toggle checked={form.roster_auto_sync_enabled} onChange={v => set('roster_auto_sync_enabled', v)} />
          </Row>
          {form.roster_auto_sync_enabled && (
            <Row isMobile={isMobile} label="Count as a current student if their last booking was within"
                 hint="Older than this, and we leave them off your roster — add them yourself in Students if that's wrong.">
              <NumberInput value={form.roster_auto_sync_days} onChange={v => set('roster_auto_sync_days', v)} suffix="days" />
            </Row>
          )}
        </Card>
      )}

      {!isGo && !isFill && (<>
      <Card title="Working hours & schedule">
        <Row isMobile={isMobile} label="Working hours"
             hint="Your standard start and finish each day. Bookable slots fall within this window.">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TimeInput value={form.start_time} onChange={v => set('start_time', v)} />
            <span style={{ color: C.muted, fontSize: 13 }}>to</span>
            <TimeInput value={form.end_time} onChange={v => set('end_time', v)} />
          </div>
        </Row>

        <div style={{ padding: '14px 0', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 14, color: C.text }}>Days you work</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 3, marginBottom: 10 }}>
            Tap to toggle. Greyed-out days are days off — no bookings offered.
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {WEEK_DAYS.map(d => {
              const off = form.days_off_set.has(d.code);
              return (
                <button key={d.code} type="button"
                  onClick={() => {
                    const next = new Set(form.days_off_set);
                    if (off) next.delete(d.code); else next.add(d.code);
                    set('days_off_set', next);
                  }}
                  style={{
                    flex: 1, minWidth: 42, padding: '10px 0', borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${off ? C.border : C.teal}`,
                    background: off ? 'none' : C.teal,
                    color: off ? C.faint : C.bg, fontSize: 13, fontWeight: 600
                  }}>
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Short days — optional early finish per working day */}
        <div style={{ padding: '14px 0', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 14, color: C.text }}>Short days</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 3, marginBottom: 10 }}>
            Optional: finish earlier on certain days (e.g. Saturdays). Leave blank to use your normal finish.
          </div>
          {WEEK_DAYS.filter(d => !form.days_off_set.has(d.code)).map(d => (
            <div key={d.code} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
              <span style={{ fontSize: 13, color: C.muted }}>{d.label}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <TimeInput
                  value={form.overrides[d.code] || ''}
                  onChange={v => {
                    const next = { ...form.overrides };
                    if (v) next[d.code] = v; else delete next[d.code];
                    set('overrides', next);
                  }}
                />
                {form.overrides[d.code] && (
                  <button type="button"
                    onClick={() => { const next = { ...form.overrides }; delete next[d.code]; set('overrides', next); }}
                    style={{ background: 'none', border: 'none', color: C.red, fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>
                    ×
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Lunch — optional, whole-hour only (slot engine limitation).
            tight: keep the toggle inline beside its label even on mobile. */}
        <Row isMobile={isMobile} tight label="Fixed lunch break"
             hint="If you always stop for lunch at the same time. Leave off if you take it around bookings.">
          <Toggle checked={form.lunch_enabled} onChange={v => set('lunch_enabled', v)} />
        </Row>
        {form.lunch_enabled && (
          <Row isMobile={isMobile} label="Lunch time"
               hint="On the hour only for now.">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <HourSelect value={form.lunch_start} onChange={v => set('lunch_start', v)} />
              <span style={{ color: C.muted, fontSize: 13 }}>to</span>
              <HourSelect value={form.lunch_end} onChange={v => set('lunch_end', v)} />
            </div>
          </Row>
        )}
      </Card>

      <Card title="Taking on students">
        <Row isMobile={isMobile} tight label="Accepting new students"
             hint="When off, anyone using your code is politely told you're not taking students right now.">
          <Toggle checked={form.accepting_new_students} onChange={v => set('accepting_new_students', v)} />
        </Row>
        <Row isMobile={isMobile} label="Maximum active students"
             hint="Cap your intake. Leave blank for no limit.">
          <NumberInput value={form.max_active_students} onChange={v => set('max_active_students', v)} placeholder="∞" suffix="students" />
        </Row>
        <Row isMobile={isMobile} tight label="Approve students before they can book"
             hint="New sign-ups wait for your OK before they can book a lesson.">
          <Toggle checked={form.require_approval} onChange={v => set('require_approval', v)} />
        </Row>
      </Card>

      <Card title="Booking rules">
        <Row isMobile={isMobile} tight label="Allow online booking"
             hint="Master switch for students booking themselves on WhatsApp.">
          <Toggle checked={form.online_booking_enabled} onChange={v => set('online_booking_enabled', v)} />
        </Row>
        <Row isMobile={isMobile} label="Minimum notice"
             hint="How soon before a lesson a student can still book it. 0 = right up to the start.">
          <NumberInput value={form.min_notice_hours} onChange={v => set('min_notice_hours', v)} suffix="hours" />
        </Row>
        <Row isMobile={isMobile} label="How far ahead students can book"
             hint="Students can't book beyond this many days from today.">
          <NumberInput value={form.max_advance_days} onChange={v => set('max_advance_days', v)} suffix="days" />
        </Row>
        <Row isMobile={isMobile} label="Gap between lessons"
             hint="Free time kept after each lesson for travel between pickups.">
          <NumberInput value={form.buffer_minutes} onChange={v => set('buffer_minutes', v)} suffix="mins" />
        </Row>
        <Row isMobile={isMobile} label="Max lessons per student each week"
             hint="Stops one student taking over your week. Leave blank for no limit.">
          <NumberInput value={form.max_lessons_per_week_per_student} onChange={v => set('max_lessons_per_week_per_student', v)} placeholder="∞" suffix="/week" />
        </Row>
      </Card>

      <Card title="Cancellations">
        <Row isMobile={isMobile} label="Cancellation cutoff"
             hint="Students can't cancel or reschedule within this window — they'll be told to contact you directly.">
          <NumberInput value={form.cancel_cutoff_hours} onChange={v => set('cancel_cutoff_hours', v)} suffix="hours" />
        </Row>
      </Card>

      </>)}

      <Card title="Deposits & payments">
        <Row isMobile={isMobile} tight label="Require a deposit to book"
             hint="When on, students are asked to pay a deposit after being approved, before you commit to the lesson.">
          <Toggle checked={form.deposit_enabled} onChange={v => set('deposit_enabled', v)} />
        </Row>

        {form.deposit_enabled && (
          <>
            <Row isMobile={isMobile} label="Deposit amount"
                 hint="A flat amount each student pays to secure a booking.">
              <DecimalInput value={form.deposit_amount} onChange={v => set('deposit_amount', v)} suffix="€" placeholder="20" />
            </Row>

            <div style={{ padding: '14px 0 8px' }}>
              <div style={{ fontSize: 14, color: C.text, marginBottom: 3 }}>How students pay</div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 10, lineHeight: 1.4 }}>
                Pick one — Revolut needs you to confirm each payment yourself; card deposits confirm automatically.
              </div>
              <div style={{
                display: 'inline-flex', border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden'
              }}>
                {[['revolut', 'Revolut'], ['stripe', 'Card (Stripe)']].map(([id, label]) => (
                  <button key={id} type="button" onClick={() => setDepositMethod(id)}
                    style={{
                      background: depositMethod === id ? C.teal : 'none', border: 'none',
                      color: depositMethod === id ? C.bg : C.muted,
                      fontSize: 13.5, fontWeight: 600, padding: '10px 18px',
                      cursor: 'pointer', whiteSpace: 'nowrap'
                    }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {depositMethod === 'revolut' ? (
              <Row isMobile={isMobile} label="Your Revolut link"
                   hint="Students are sent here to pay you directly — Visionry never handles the money. Find it in Revolut → Profile → share your @RevTag or revolut.me link.">
                <TextInput value={form.revolut_link} onChange={v => set('revolut_link', v)} placeholder="revolut.me/yourname" isMobile={isMobile} />
              </Row>
            ) : (
              <div style={{ padding: '14px 0' }}>
                {stripeSummaryLoading ? (
                  <div style={{ fontSize: 13, color: C.muted }}>Checking your Stripe status…</div>
                ) : stripeSummary ? (
                  <div style={{
                    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14
                  }}>
                    {stripeSummary.chargesEnabled ? (
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: '#06231a', color: C.green, borderRadius: 8,
                        fontSize: 13, fontWeight: 600, padding: '6px 12px'
                      }}>
                        Active ✓
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={handleStripeOnboard}
                        disabled={stripeBusy}
                        style={{
                          background: 'none', border: 'none', padding: 0,
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          color: '#ffb84d', fontSize: 13, fontWeight: 600,
                          cursor: stripeBusy ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {stripeBusy ? 'Opening Stripe…' : 'Finish your Stripe setup →'}
                      </button>
                    )}

                    <div style={{ display: 'flex', gap: 24, marginTop: 14, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: C.tealSoft }}>
                          {formatEuro(stripeSummary.available)}
                        </div>
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Collected</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: C.tealSoft }}>
                          {formatEuro(stripeSummary.pending)}
                        </div>
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Pending payout</div>
                      </div>
                    </div>

                    {stripeSummary.nextPayoutAmount != null && (
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>
                        {stripeSummary.nextPayoutStatus === 'paid' ? 'Last payout' : 'Next payout'}:{' '}
                        {formatEuro(stripeSummary.nextPayoutAmount)}
                        {stripeSummary.nextPayoutDate ? ` · ${formatPayoutDate(stripeSummary.nextPayoutDate)}` : ''}
                      </div>
                    )}

                    {stripeSummary.dashboardUrl && (
                      <a
                        href={stripeSummary.dashboardUrl} target="_blank" rel="noreferrer"
                        style={{
                          display: 'inline-block', marginTop: 14,
                          background: 'none', border: `1px solid ${C.border}`, borderRadius: 8,
                          color: C.tealSoft, fontSize: 13, fontWeight: 600,
                          padding: '9px 14px', textDecoration: 'none'
                        }}
                      >
                        View my payments in Stripe →
                      </a>
                    )}
                  </div>
                ) : stripeOnboarded ? (
                  // Summary call failed but we already know (from the DB, or
                  // an earlier successful check) that Stripe is active —
                  // degrade to the flat badge rather than show nothing.
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: '#06231a', color: C.green, borderRadius: 8,
                    fontSize: 13, fontWeight: 600, padding: '8px 14px'
                  }}>
                    Card deposits active ✓
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleStripeOnboard}
                    disabled={stripeBusy}
                    style={{
                      background: C.teal, border: 'none', borderRadius: 8, color: C.bg,
                      fontSize: 14, fontWeight: 600, padding: '10px 18px', minHeight: 44,
                      cursor: stripeBusy ? 'not-allowed' : 'pointer', opacity: stripeBusy ? 0.7 : 1
                    }}
                  >
                    {stripeBusy ? 'Opening Stripe…' : 'Set up card deposits'}
                  </button>
                )}
                {stripeNote && (
                  <div style={{ marginTop: 8, fontSize: 12.5, color: C.red }}>{stripeNote}</div>
                )}
              </div>
            )}
          </>
        )}
      </Card>

      {/* Save bar — fixed to the bottom of the viewport so it's always
          reachable, even on a long settings page. The content's paddingBottom
          above keeps the last card from being hidden behind it. */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: C.card, borderTop: `1px solid ${C.border}`,
        padding: '12px 20px calc(12px + env(safe-area-inset-bottom))',
        zIndex: 50
      }}>
        <div style={{
          maxWidth: 800, margin: '0 auto',
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap'
        }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: C.teal, border: 'none', borderRadius: 8,
              color: C.bg, fontSize: 15, fontWeight: 600, minHeight: 44,
              padding: '12px 28px', cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1
            }}
          >
            {saving ? 'Saving…' : 'Save settings'}
          </button>
          {status && (
            <span style={{ fontSize: 13, color: status.type === 'ok' ? C.green : C.red }}>
              {status.msg}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}